import React, { useEffect, useRef, useState } from "react";
import Marquee from "react-fast-marquee";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

export const BLOG_BASE = "https://blog.jaaga.ai";
// Same-origin path, proxied to the blog's real posts API by the `/blog-api/`
// location in nginx/nginx.conf (and the matching Vite dev proxy). Fetching
// cross-origin here used to throw a CORS error on every page load — and
// `${BLOG_BASE}/api/posts` is a 404 regardless, so the list below was always
// what rendered. Article links still point at BLOG_BASE, which is public.
export const POSTS_API = "/blog-api/posts";

// Bundled snapshot of the live posts, used if the runtime fetch to the blog
// API is blocked (e.g. CORS). The section still fetches fresh data on mount
// and replaces this list when the request succeeds.
export const FALLBACK_POSTS = [
  {
    slug: "property-card-online-7-12-download-maharashtra",
    category: "property-documents",
    featuredImage: "/blog-maharashtra-property-documents.png",
    title:
      "Property Card Online, 7/12 Utara Download & Maharashtra Land Records",
    excerpt:
      "Access Maharashtra property and land records online with JaaGa. Download Property Card, 7/12 Utara, Ferfar, 8A Extract, Bhunaksha, Village Map, EC, RERA details, and more.",
  },
  {
    slug: "how-to-research-land-in-telangana",
    category: "legal-verification",
    featuredImage: "/blog-telangana-land-research.png",
    title: "How to Research Land in Telangana: A Step-by-Step Guide",
    excerpt:
      "Learn how to research land in Telangana before buying or investing. Discover land records, ownership verification, encumbrance certificates, and expert tips to avoid disputes.",
  },
  {
    slug: "red-flags-property-documents-lawyers-must-never-miss",
    category: "legal-verification",
    featuredImage:
      "https://ik.imagekit.io/sravanth/ChatGPT%20Image%20May%2022,%202026,%2005_02_02%20PM.png",
    title: "15 Red Flags in Property Documents Lawyers Must Never Miss",
    excerpt:
      "Discover 15 critical red flags in property documents every lawyer must verify before issuing a title opinion. Learn how modern legal teams handle property due diligence.",
  },
  {
    slug: "title-search-report-and-legal-opinion-guide",
    category: "legal-verification",
    featuredImage:
      "https://ik.imagekit.io/sravanth/blog%20thumbnails%20(6).png",
    title:
      "Title Search Report and Legal Opinion: 9 Crucial Checks for Panel Lawyers",
    excerpt:
      "Learn how a title search report and legal opinion help panel lawyers verify ownership, detect encumbrances, reduce legal risks, and ensure secure property transactions.",
  },
  {
    slug: "rera-has-become-a-shield-for-defaulting-builders-not-homebuyers-supreme-court-flags-institutional-failure",
    category: "legal-verification",
    featuredImage:
      "https://ik.imagekit.io/sravanth/Untitled%20design%20(26).png",
    title:
      "RERA's Double-Edged Sword: Supreme Court Flags Institutional Failure",
    excerpt:
      "Supreme Court flags institutional failure as RERA becomes a shield for defaulting builders, not homebuyers. Learn about the challenges.",
  },
  {
    slug: "download-telangana-ec-ror-pahani-jaaga-app",
    category: "property-documents",
    featuredImage:
      "https://ik.imagekit.io/sravanth/blog%20thumbnails%20(5).png",
    title: "Download Telangana EC, Land Records, RoR & Pahani Using JaaGa App",
    excerpt:
      "Learn how to download Telangana EC, Land Records, RoR & Pahani instantly using the JaaGa App. Understand document importance and how to access them online.",
  },
  {
    slug: "telangana-government-core-urban-act-cure-ghmc-replacement",
    category: "property-audit",
    featuredImage: "https://ik.imagekit.io/jaaga/blog%20thumbnails%20(3).png",
    title: "Telangana to Replace GHMC Act with Core Urban Act (CURE)",
    excerpt:
      "Learn why the Telangana government is replacing the GHMC Act with a new Core Urban Act (CURE), and how it will affect Hyderabad's civic administration and urban development.",
  },
  {
    slug: "encumbrance-certificate-guide-india",
    category: "property-documents",
    featuredImage: "https://ik.imagekit.io/jaaga/blog%20thumbnails%20(2).png",
    title:
      "Encumbrance Certificate (EC): The Ultimate Guide for Property Buyers",
    excerpt:
      "Learn everything about the Encumbrance Certificate (EC), its importance in property transactions, and how to download it online in states like Telangana, AP, Tamil Nadu, and Odisha.",
  },
  {
    slug: "how-to-download-encumbrance-certificate-ec-in-telangana",
    category: "property-documents",
    featuredImage: null,
    title:
      "How to Download Certified Encumbrance Certificate (EC) in Telangana",
    excerpt:
      "A Certified Encumbrance Certificate (EC) records all property transactions. Learn how to download your Certified EC in Telangana using JaaGa.",
  },
];

export const normalizeImg = (src) =>
  src ? (src.startsWith("http") ? src : `${BLOG_BASE}${src}`) : null;

export const cleanTitle = (t) =>
  t ? t.replace(/\s*\|\s*JaaGa\s*$/i, "").trim() : t;

const prettyCategory = (c) => (c ? c.replace(/-/g, " ") : "Article");

// Branded default shown when a post has no image OR its image fails to load,
// so a card never renders a broken-image icon.
const BlogImgFallback = () => (
  <span className="blog-card-img-fallback">
    <Newspaper size={26} aria-hidden="true" />
    JaaGa Insights
  </span>
);

const BlogCard = ({ post }) => {
  const img = normalizeImg(post.featuredImage);
  const [failed, setFailed] = useState(false);
  const showImg = img && !failed;
  return (
    <a
      href={`${BLOG_BASE}/blogs/${post.slug}`}
      target="_blank"
      rel="noreferrer"
      className="blog-card"
    >
      <div className="blog-card-img">
        {showImg ? (
          <img
            src={img}
            alt={cleanTitle(post.title)}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        ) : (
          <BlogImgFallback />
        )}
        <span className="blog-card-tag">{prettyCategory(post.category)}</span>
      </div>
      <div className="blog-card-body">
        <h3 className="blog-card-title">{cleanTitle(post.title)}</h3>
        <p className="blog-card-excerpt">{post.excerpt}</p>
        <span className="blog-card-read">
          Read article
          <ArrowUpRight size={14} />
        </span>
      </div>
    </a>
  );
};

const BlogSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [posts, setPosts] = useState(FALLBACK_POSTS);
  // Phones get a swipeable row that starts flush with the page padding; the
  // continuous marquee always had a card cut off at the left edge there.
  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    let active = true;
    fetch(POSTS_API)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (active && Array.isArray(data) && data.length) setPosts(data);
      })
      .catch(() => {
        /* keep the bundled fallback */
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      className="jh-section"
      style={{ background: "#eef1f6", paddingTop: 0, paddingBottom: 0 }}
    >
      <div className="jh-wrap" ref={ref}>
        <motion.div
          className="jh-section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="jh-eyebrow jh-center">JaaGa Insights</p>
          <h2>
            From our <span style={{ color: "var(--jh-gold)" }}>Blog</span>
          </h2>
          <p className="sm:whitespace-nowrap" style={{ maxWidth: "none" }}>
            Simple guides, legal explainers, and state-by-state property tips
            from the JaaGa team.
          </p>
        </motion.div>
      </div>

      {/* Continuously moving blog cards, full-bleed (tablet/desktop); a
          swipeable, snap-aligned row on phones. */}
      {isMobile ? (
        <div className="jh-swipe-row" aria-label="Blog articles">
          {posts.map((post, i) => (
            <BlogCard key={post.slug || i} post={post} />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: "clip" }}>
          <Marquee
            speed={40}
            pauseOnHover
            gradient={false}
            loop={0}
            style={{ paddingTop: 10, paddingBottom: 10 }}
          >
            {posts.map((post, i) => (
              <BlogCard key={post.slug || i} post={post} />
            ))}
          </Marquee>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 36, paddingBottom: 48 }}>
        <a
          href={`${BLOG_BASE}/blogs`}
          target="_blank"
          rel="noreferrer"
          className="blog-view-all"
        >
          View all articles
          <ArrowUpRight size={15} />
        </a>
      </div>

      <style>{`
        .blog-card {
          display: flex;
          flex-direction: column;
          width: 340px;
          height: 392px;
          flex-shrink: 0;
          margin-right: 22px;
          background: var(--jh-surface);
          border: 1px solid var(--jh-border);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: var(--jh-shadow-card);
          text-decoration: none;
          color: inherit;
          white-space: normal;
          transition: transform 0.22s ease, box-shadow 0.22s ease,
            border-color 0.22s ease;
        }
        .blog-card:hover {
          transform: translateY(-5px);
          border-color: var(--jh-gold-border);
          box-shadow: var(--jh-shadow-lg);
        }
        .blog-card-img {
          position: relative;
          height: 184px;
          flex-shrink: 0;
          background: linear-gradient(135deg, #0c2244 0%, #153568 60%, #1e3a5f 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .blog-card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .blog-card-img-fallback {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.04em;
        }
        .blog-card-img-fallback svg {
          color: rgba(255, 255, 255, 0.9);
        }
        .blog-card-tag {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(255, 255, 255, 0.92);
          color: var(--jh-navy);
          border-radius: 99px;
          padding: 4px 11px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: capitalize;
          backdrop-filter: blur(4px);
        }
        .blog-card-body {
          display: flex;
          flex-direction: column;
          gap: 9px;
          padding: 18px 20px 20px;
          flex: 1;
        }
        .blog-card-title {
          font-size: 16px;
          font-weight: 700;
          line-height: 1.3;
          color: var(--jh-navy);
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-card-excerpt {
          font-size: 13px;
          line-height: 1.6;
          color: var(--jh-text-mid);
          font-weight: 300;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-card-read {
          margin-top: auto;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: var(--jh-gold);
          text-transform: uppercase;
        }
        .blog-view-all {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: var(--jh-navy);
          color: #fff;
          border-radius: 99px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          transition: transform 0.18s ease;
        }
        .blog-view-all:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </section>
  );
};

export default BlogSection;
